import React, { useEffect } from "react";
import { createPortal } from "react-dom";
import { SavedDocument } from "../../models/document-storage.types";

interface DocumentDeleteConfirmProps {
  document: SavedDocument;
  isDeleting: boolean;
  error: string | null;
  onConfirm: () => void;
  onCancel: () => void;
}

/**
 * Confirmation modal that blocks deletion until the user confirms.
 * Mirrors the WelcomeModal portal/escape/backdrop pattern.
 */
export const DocumentDeleteConfirm: React.FC<DocumentDeleteConfirmProps> = ({
  document: doc,
  isDeleting,
  error,
  onConfirm,
  onCancel,
}) => {
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !isDeleting) {
        onCancel();
      }
    };
    document.body.style.overflow = "hidden";
    document.addEventListener("keydown", handleEscape);
    return () => {
      document.removeEventListener("keydown", handleEscape);
      document.body.style.overflow = "unset";
    };
  }, [onCancel, isDeleting]);

  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget && !isDeleting) {
      onCancel();
    }
  };

  const modalContent = (
    <div className="document-modal-backdrop" onClick={handleBackdropClick}>
      <div
        className="document-modal document-modal--confirm"
        role="dialog"
        aria-modal="true"
        aria-labelledby="document-delete-title"
      >
        <h2 id="document-delete-title" className="document-modal__title">
          Delete document?
        </h2>
        <p className="document-modal__body">
          “{doc.title}” will be permanently deleted. This can’t be undone.
        </p>

        {error && <div className="form-error-message">{error}</div>}

        <div className="document-modal__actions">
          <button
            type="button"
            className="document-modal__btn document-modal__btn--secondary"
            onClick={onCancel}
            disabled={isDeleting}
          >
            Cancel
          </button>
          <button
            type="button"
            className="document-modal__btn document-modal__btn--danger"
            onClick={onConfirm}
            disabled={isDeleting}
          >
            {isDeleting ? "Deleting…" : "Delete"}
          </button>
        </div>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
};
